import { CaretRightOutlined } from "@ant-design/icons";
import { useLazyQuery, useQuery } from "@apollo/react-hooks";
import get from "lodash/get";

import {
    Button,
    Card,
    Checkbox,
    Collapsible,
    DataTable,
    Modal,
    Scrollable,
    Spinner,
    Stack,
} from "@shopify/polaris";
import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useReducer,
    useRef,
    useState,
} from "react";
import styled from "styled-components";
import {
    REPORT_SALE_BY_STORE_SUMMARIES,
    REPORT_SALE_STORES,
} from "../../../graphql/queries";
import {
    arrInvalid,
    checkRole,
    formatter,
    generateCsvFileName,
    getCurrencyPreAndSuffix,
    getUnique,
    isPME_TEAM,
    objectInvalid,
    reducerFn,
    removeEl,
} from "../../../helper";
import { EmptyStatePolaris } from "../../shared/EmptyStatePolaris";
import { useReportsContext } from "../context";
import { useOwnerStore } from "../hooks/useOwnerStore";
import { ComparedPercent } from "./ComparedPercent";
import { useCollapseContext } from "./LayoutSection";
import ShowCard, { conditionByFilter } from "./ShowCard";
import { genTable } from "../utils";
import { ImportMinor } from "@shopify/polaris-icons";
import { FilterCustomTimePolaris } from "../../tasks/FilterCustomTimePolaris";
import CustomTimeInner from "../../filters/CustomTimeInner";
import moment from "moment";
import dayjs from "dayjs";
import { useAppContext } from "../../../context";

const collapseKey = "storeSummary";
export default function StoreSummary({ saleChanel }) {
    const { currentUser } = useAppContext();
    const isPMETeam = isPME_TEAM(currentUser);
    const { isAdministrator, isSeller, isStoreManager, isMarketplaceManager } =
        checkRole(currentUser);

    // Context
    const {
        range,
        filter: filterCtx,
        isCompare,
        rangeToCompare,
        loading: loadingRoot,
        teamID,
    } = useReportsContext();

    const { collapseActive = {}, setCollapseActive = () => {} } =
        useCollapseContext() || {};
    const open = !!collapseActive[collapseKey];

    // State
    const [filter, setFilter] = useState({
        limit: 20,
        offset: 0,
        search: null,
        saleChanel: "etsy",
        month: null,
        productBaseIds: null,
        collectionIds: null,
        tagIds: null,
        storeManagerID: null,
    });
    const [state, setState] = useReducer(reducerFn, {
        rows: [],
        total: 0,
        totalSale: 0,
        totalCost: 0,
        totalRevenues: 0,
        totalKpis: 0,

        loadMore: false,
        cantLoad: false,
        loadOwnerStore: false,
    });
    const [stateCompare, setStateCompare] = useState({
        totalSale: 0,
        totalCost: 0,
        totalRevenues: 0,
    });
    const timeoutRef = useRef(null);

    const [getOwnerStores, ownerStoreState] = useOwnerStore(filter);

    // Queries
    const { data, loading, fetchMore } = useQuery(
        REPORT_SALE_BY_STORE_SUMMARIES,
        {
            variables: {
                filter,
            },
            fetchPolicy: "no-cache",
            skip:
                !isAdministrator &&
                (isSeller || isStoreManager || isMarketplaceManager) &&
                !isPMETeam,
        },
    );

    useQuery(REPORT_SALE_BY_STORE_SUMMARIES, {
        variables: {
            filter: {
                ...filter,
                ...(filter.month ? { limit: 1000, offset: 0 } : {}),
            },
        },
        onCompleted: (res) => {
            const total = getTotal(res);
            setState((p) => ({ ...p, ...total }));
        },
        fetchPolicy: "no-cache",
        skip:
            !isAdministrator &&
            (isSeller || isStoreManager || isMarketplaceManager) &&
            !isPMETeam,
    });

    const [lazyReport, { data: dataC, loading: loadingC, error: errorC }] =
        useLazyQuery(REPORT_SALE_STORES);

    // Get data

    useEffect(() => {
        if (filterCtx != null) {
            let { storeID, ...ignoredFilter } = filterCtx;
            setFilter((prev) => ({ ...prev, ...ignoredFilter }));
        }
    }, [filterCtx]);

    useEffect(() => {
        setFilter((prev) => ({
            ...prev,
            //   filterByTime: range,
            teamId: teamID ?? undefined,
        }));
        setState({ cantLoad: false });
    }, [range, teamID]);

    // const rowsRef = useRef(new Set());
    useEffect(() => {
        if (state.loadOwnerStore) {
            const storeIDs = getStoreIDs(data);
            if (storeIDs.length > 0) {
                getOwnerStores(storeIDs);
            }
        }
    }, [state.loadOwnerStore, data]);

    const tableRef = useRef(null);
    useEffect(() => {
        const nodes = data?.reportSaleByStoreSummaries?.nodes;
        const total = data?.reportSaleByStoreSummaries?.total;
        let totalKpis = 0;

        const newItems =
            nodes?.length > 0
                ? nodes.map((node, index) => {
                      const totalSale = node?.totalSale;
                      const totalBaseCost = node?.totalBaseCost || 0;
                      const totalRevenues = node?.totalRevenues || 0;
                      const store = node?.store;
                      const title = store?.title;
                      const storeId = node?.storeId;

                      const currency = node?.currency;

                      const netProfit = formatNumber(node?.netProfit, currency);
                      const sales = formatNumber(node?.sales, currency);
                      const totalSales = formatNumber(
                          node?.totalSales,
                          currency,
                      ); // Total sales
                      const refunds = formatNumber(node?.refunds, currency); // Refunds
                      const salesTaxPaidByBuyer = formatNumber(
                          node?.salesTaxPaidByBuyer,
                          currency,
                      ); // Sales tax paid by buyer
                      const vatPaidByBuyer = formatNumber(
                          node?.vatPaidByBuyer,
                          currency,
                      ); // VAT paid by buyer
                      const buyerFee = formatNumber(node?.buyerFee, currency); // Buyer Fee
                      const fees = formatNumber(node?.fees, currency);
                      const marketing = formatNumber(node?.marketing, currency);

                      const deposit = formatNumber(node?.deposit, currency);

                      let {
                          total,
                          totalKPIOnline,
                          totalKPIOffline,
                          totalKPIBot,
                      } = node?.totalKpis || {};
                      totalKpis += total || 0;

                      total = Number(total || 0).toFixed(2);
                      totalKPIOnline = Number(totalKPIOnline || 0).toFixed(2);
                      totalKPIOffline = Number(totalKPIOffline || 0).toFixed(2);
                      totalKPIBot = Number(totalKPIBot || 0).toFixed(2);

                      const newRows = [
                          <div className="col-index">{index + 1}</div>,
                          <div className="store-name">
                              <span>{title || storeId}</span>
                          </div>,
                          <div className="total-sale">
                              <span>{netProfit}</span>
                          </div>,
                          <div className="total-sale">
                              <span>{totalSales}</span>
                          </div>,
                          <div className="total-sale">
                              <span>{salesTaxPaidByBuyer}</span>
                          </div>,
                          <div className="total-sale">
                              <span>{refunds}</span>
                          </div>,
                          <div className="total-sale">
                              <span>{sales}</span>
                          </div>,
                          <div className="total-sale">
                              <span>{deposit}</span>
                          </div>,
                          <div className="total-sale">
                              <span>{fees}</span>
                          </div>,
                          <div className="total-sale">
                              <span>{marketing}</span>
                          </div>,
                          <div className="total-sale">
                              <span>{totalSale}</span>
                          </div>,
                          <div className="total-cost">
                              <span>{formatter.format(totalBaseCost)}</span>
                          </div>,
                          <div className="total-revenue">
                              <span>{formatter.format(totalRevenues)}</span>
                          </div>,
                          <div className="total-kpi">
                              <span>{total}</span>
                          </div>,
                          <div className="total-kpi-offline">
                              <span>{totalKPIOffline}</span>
                          </div>,
                          <div className="total-kpi-online">
                              <span>{totalKPIOnline}</span>
                          </div>,
                          <div className="total-kpi-bot">
                              <span>{totalKPIBot}</span>
                          </div>,
                      ];

                      return newRows;
                      //   return [
                      //       newRows,
                      //       [
                      //             <div
                      //                 data-store-id={`${storeId}`}
                      //                 style={{ display: "none" }}
                      //                 className="owner-store-slot"
                      //                 ref={(node) => rowsRef.current.add(node)}
                      //             />,
                      //       ],
                      //   ];
                  })
                : //   .flat()
                  [];

        setState((prev) => ({
            ...prev,
            rows: newItems,
            total,
            totalKpis,
        }));
    }, [data]);

    useEffect(() => {
        return; // TODO:
        if (isCompare) {
            let filterByTime = null;
            if (rangeToCompare?.from != null) {
                filterByTime = {
                    ...filter.filterByTime,
                    ...rangeToCompare,
                };
            }
            lazyReport({
                variables: {
                    filter: {
                        ...filter,
                        filterByTime,
                    },
                },
            });
        }
    }, [rangeToCompare, filter, isCompare]);

    // useLayoutEffect(() => {
    // if (rowsRef.current.size) {
    //     for (let row of Array.from(rowsRef.current.values())) {
    //         if (!row || !row.parentNode) continue;
    //         const storeId = row.getAttribute("data-store-id");
    //         if (
    //             row.parentNode &&
    //             !row.parentNode.classList.contains("slot")
    //         ) {
    //             row.parentNode.classList.add("slot");
    //             row.parentNode.setAttribute("colspan", 8);
    //         }
    //         const mapStores = ownerStoreState.data[storeId];
    //         if (state.loadOwnerStore) {
    //             if (mapStores?.length > 0) {
    //                 genTable(row.parentNode, mapStores, genOwnerInfo);
    //             }
    //         } else {
    //             if (row.parentNode) {
    //                 const table = row.parentNode.querySelector("table");
    //                 if (table) {
    //                     removeEl(table);
    //                 }
    //             }
    //         }
    //     }
    // }
    // }, [rowsRef.current.size, ownerStoreState.data, state.loadOwnerStore]);

    useEffect(() => {
        return; // TODO:
        if (isCompare) {
            const totalSale = dataC?.reportSaleByStoreSummaries?.totalSale;
            const totalBaseCost =
                dataC?.reportSaleByStoreSummaries?.totalBaseCost;
            const totalRevenues =
                dataC?.reportSaleByStoreSummaries?.totalRevenues;
            setStateCompare((prev) => ({
                ...prev,
                totalSale,
                totalCost: totalBaseCost,
                totalRevenues,
            }));
        } else {
            setStateCompare({ totalSale: 0 });
        }
    }, [dataC, isCompare]);

    // Handles
    const handleScroll = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef);
        }
        if (!state.cantLoad) {
            setState({ loadMore: true });
            timeoutRef.current = setTimeout(() => {
                fetchMore({
                    variables: {
                        filter: {
                            ...filter,
                            offset: data.reportSaleByStoreSummaries.nodes
                                .length,
                        },
                    },
                    updateQuery: (prev, { fetchMoreResult, variables }) => {
                        if (!fetchMoreResult) return prev;
                        const reportSaleByStoreSummaries =
                            fetchMoreResult.reportSaleByStoreSummaries;
                        const nodes = reportSaleByStoreSummaries.nodes || [];

                        const filter = variables.filter;
                        const limit = filter.limit;
                        setState({ loadMore: false });
                        if (nodes.length < limit) {
                            setState({ cantLoad: true });
                        }

                        const newNodes = [
                            ...prev.reportSaleByStoreSummaries.nodes,
                            ...nodes,
                        ];

                        const result = getUnique(newNodes, "storeId");

                        const totalCost = (result || []).reduce((acc, item) => {
                            if (item?.totalBaseCost > 0)
                                acc += item.totalBaseCost;
                            return acc;
                        }, 0);

                        const totalRevenues = (result || []).reduce(
                            (acc, item) => {
                                if (item?.totalRevenues > 0)
                                    acc += item.totalRevenues;
                                return acc;
                            },
                            0,
                        );

                        setState((p) => ({
                            ...p,
                            totalCost: totalCost.toFixed(2),
                            totalRevenues: totalRevenues.toFixed(2),
                        }));

                        return {
                            ...prev,
                            reportSaleByStoreSummaries: {
                                ...prev.reportSaleByStoreSummaries,
                                nodes: result,
                            },
                        };
                    },
                });
            }, 500);
        }
    }, [data, filter, state.cantLoad, fetchMore]);

    const con = conditionByFilter(filterCtx, ["storeID"]);

    const btnRef = useRef(null);
    const spinnerMarkup = (
        <Stack distribution="center">
            <Spinner size="small" />
        </Stack>
    );

    const handleTriggerBtn = useCallback(() => {
        btnRef.current && btnRef.current.click();
    }, []);

    const handleToggle = useCallback(() => {
        setCollapseActive((p) => ({ ...p, [collapseKey]: !p[collapseKey] }));
    }, [collapseKey, setCollapseActive]);

    const handleLoadOwnerStore = useCallback((val) => {
        setState({ loadOwnerStore: val });
    }, []);

    const subTitleMarkup = useMemo(
        () => (
            <div style={{ display: "flex", flexDirection: "column" }}>
                <span>
                    <span>{state.totalSale} Line items</span>
                    <ComparedPercent
                        originalVal={state.totalSale}
                        newVal={stateCompare.totalSale}
                    />
                </span>

                <span>
                    <span>Total cost: {formatter.format(state.totalCost)}</span>
                    <ComparedPercent
                        originalVal={state.totalCost}
                        newVal={stateCompare.totalCost}
                    />
                </span>
                <span>
                    <span>
                        Total revenue: {formatter.format(state.totalRevenues)}
                    </span>
                    <ComparedPercent
                        originalVal={state.totalRevenues}
                        newVal={stateCompare.totalRevenues}
                    />
                </span>

                <span>
                    <span>
                        Total KPI: {Number(state.totalKpis || 0).toFixed(2)}
                    </span>
                </span>
            </div>
        ),
        [
            state.totalSale,
            stateCompare.totalSale,
            state.totalCost,
            state.totalRevenues,
            stateCompare.totalRevenues,
            stateCompare.totalCost,
            stateCompare.totalKpis,
        ],
    );

    const [month, setMonth] = useState(null);
    useEffect(() => {
        const now = new Date(Date.now());
        const today = dayjs(now);
        let todayFormatter = moment(now).format("MMMM YYYY");
        setMonth(today);

        todayFormatter = todayFormatter
            ? todayFormatter.split(" ").join(", ")
            : todayFormatter;

        setFilter((p) => ({ ...p, month: todayFormatter }));
    }, []);

    const [monthFilter, setMonthFilter] = useState(undefined);
    const handleCustomTime = () => {};
    const [openModel, setOpenModel] = useState(false);

    const handleClose = useCallback(() => {
        setOpenModel(false);
    }, []);

    const headerMarkup = useMemo(
        () => (
            <Stack spacing="loose">
                <Stack.Item fill>
                    <div>
                        <HeadingWrap>
                            <span className="btn-wrap">
                                <CaretRightOutlined
                                    rotate={open ? 90 : 0}
                                    style={{ color: "#000" }}
                                    ref={btnRef}
                                    onClick={handleToggle}
                                />
                            </span>
                            <h2
                                className="Polaris-Heading"
                                onClick={handleTriggerBtn}
                            >
                                Etsy Store Summary
                            </h2>
                        </HeadingWrap>

                        <div style={{ maxWidth: 200, marginTop: 10 }}>
                            <CustomTimeInner
                                value={month}
                                views={["year", "month"]}
                                label="Select Time"
                                onChange={(value, date) => {
                                    if (!date || typeof date !== "string") {
                                        setMonth(null);
                                        setFilter((p) => ({
                                            ...p,
                                            month: null,
                                        }));
                                        return "";
                                    }

                                    let dateTimeFormatted =
                                        moment(date).format("MMMM YYYY");

                                    dateTimeFormatted = dateTimeFormatted
                                        ? dateTimeFormatted
                                              .split(" ")
                                              .join(", ")
                                        : dateTimeFormatted;

                                    setMonth(value);
                                    setFilter((p) => ({
                                        ...p,
                                        month: dateTimeFormatted,
                                    }));
                                }}
                            />
                        </div>
                    </div>
                </Stack.Item>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "start",
                        columnGap: ".5rem",
                    }}
                >
                    {/* <Button
                        plain
                        icon={ImportMinor}
                        onClick={() => donwloadSaleByStore(data)}
                    /> */}
                    {subTitleMarkup}
                </div>
            </Stack>
        ),
        [subTitleMarkup, open, month, state.loadOwnerStore],
    );

    return isAdministrator ||
        ((isSeller || isStoreManager || isMarketplaceManager) && isPMETeam) ? (
        <ShowCard show={con}>
            <Card>
                <Container>
                    <Card.Header title={headerMarkup} />
                    <Card.Section>
                        <div>
                            <Collapsible
                                open={open}
                                id={collapseKey}
                                transition={{
                                    duration: "500ms",
                                    timingFunction: "ease-in-out",
                                }}
                                expandOnPrint
                            >
                                {loading || loadingC || loadingRoot ? (
                                    spinnerMarkup
                                ) : state.rows?.length > 0 ? (
                                    <Scrollable
                                        shadow
                                        id={`scrollable-stores`}
                                        style={{
                                            maxHeight: "43rem",
                                            paddingRight: "1.6rem",
                                            marginRight: "-1.6rem",
                                        }}
                                        onScrolledToBottom={handleScroll}
                                    >
                                        <Stack
                                            vertical
                                            spacing="loose"
                                            wrap={false}
                                        >
                                            <div ref={tableRef}>
                                                <DataTable
                                                    rows={state.rows}
                                                    columnContentTypes={[
                                                        "text",
                                                        "text",
                                                        "text",
                                                        "text",
                                                        "text",
                                                        "text",
                                                        "text",
                                                        "text",
                                                        "text",
                                                        "text",
                                                        "text",
                                                        "text",
                                                        "text",
                                                        "text",
                                                        "text",
                                                        "text",
                                                        "text",
                                                    ]}
                                                    headings={[
                                                        "#",
                                                        "Name",
                                                        "Net Profit",
                                                        "Total Sales",
                                                        "Sales tax paid by Buyer",
                                                        "Refunds",
                                                        "Etsy Sales",
                                                        "Deposits |",
                                                        "Fees",
                                                        "Marketing",
                                                        "Sales",
                                                        "Total Cost",
                                                        "Total Revenue",
                                                        "Total KPI",
                                                        "Total KPI Offline",
                                                        "Total KPI Online",
                                                        "Total KPI Bot",
                                                    ]}
                                                    hideScrollIndicator
                                                />
                                            </div>
                                            {state.loadMore && spinnerMarkup}
                                        </Stack>
                                    </Scrollable>
                                ) : (
                                    <EmptyStatePolaris noData slim />
                                )}
                            </Collapsible>
                        </div>
                    </Card.Section>
                </Container>
            </Card>
        </ShowCard>
    ) : null;
}

//
function getStoreIDs(data) {
    const res = [];
    if (data?.reportSaleByStoreSummaries?.nodes?.length > 0) {
        res.push(
            ...data.reportSaleByStoreSummaries.nodes
                .map((i) => i.storeId)
                .filter(Boolean),
        );
    }

    return res;
}

function genTimeMarkup(data, isCreated) {
    const b = document.createElement("b");
    b.textContent = isCreated ? "Created at: " : "Deleted at: ";

    let span = "";
    const key = isCreated ? "createdAt" : "deletedAt";
    if (data[key]) {
        span = document.createElement("span");
        span.appendChild(b);

        const val = document.createTextNode(data[key]);
        span.appendChild(val);
    }
    return span;
}

function genOwnerInfo(data) {
    const createdAtMarkup = genTimeMarkup(data, true);
    const deletedAtMarkup = genTimeMarkup(data);

    const times = [createdAtMarkup, deletedAtMarkup].filter(Boolean);
    const i = document.createElement("i");
    i.style = "font-size:12px;";
    for (let t of times) {
        i.appendChild(t);
        i.appendChild(t);
    }

    if (times.length > 1) {
        i.insertBefore(document.createTextNode(" - "), deletedAtMarkup);
    }

    if (times.length > 0) {
        i.insertBefore(document.createTextNode(" ("), i.firstElementChild);
        i.appendChild(document.createTextNode(") "));
    }

    const userDiv = document.createElement("div");
    userDiv.innerHTML = `<span role="img" aria-label="minus" class="anticon anticon-minus">
  <svg
    viewBox="64 64 896 896"
    focusable="false"
    data-icon="minus"
    width="10px"
    height="10px"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M872 474H152c-4.4 0-8 3.6-8 8v60c0 4.4 3.6 8 8 8h720c4.4 0 8-3.6 8-8v-60c0-4.4-3.6-8-8-8z"></path>
  </svg>
</span> ${data.user || ""}`;
    userDiv.appendChild(i);
    userDiv.className = "store-name store-owner";
    return userDiv;
}

function getTotal(res) {
    const data = get(res, "reportSaleByStoreSummaries");
    const totalSale = get(data, "totalSale");
    const nodes = get(data, "nodes") || [];
    let totalCost = 0;
    let totalRev = 0;

    for (let item of nodes) {
        const totalBaseCost = item?.totalBaseCost || 0;
        const totalRevenues = item?.totalRevenues || 0;
        totalCost += totalBaseCost;
        totalRev += totalRevenues;
    }

    return {
        totalSale,
        totalCost: totalCost.toFixed(2),
        totalRevenues: totalRev.toFixed(2),
    };
}

function donwloadSaleByStore(data) {
    const nodes = get(data, "reportSaleByStoreSummaries.nodes") || [];

    if (arrInvalid(nodes)) return;

    let csvContent =
        "Name,Sales,Total Cost,Total Revenue,Total KPI,Total KPI Offline,Total KPI Online\n";

    for (let node of nodes) {
        if (objectInvalid(node)) continue;

        let {
            totalSale,
            totalBaseCost,
            totalRevenues,
            store,
            totalKpis,
            storeId,
        } = node;
        let { title } = store || {};
        title = title || storeId;
        let { total, totalKPIOnline, totalKPIOffline, totalKPIBot } =
            totalKpis || {};
        total = Number(total || 0).toFixed(2);
        totalKPIOnline = Number(totalKPIOnline || 0).toFixed(2);
        totalKPIOffline = Number(totalKPIOffline || 0).toFixed(2);
        totalKPIBot = Number(totalKPIBot || 0).toFixed(2);

        totalBaseCost = formatter.format(totalBaseCost);
        totalRevenues = formatter.format(totalRevenues);
        csvContent += `"${title}",${totalSale},"${totalBaseCost}","${totalRevenues}",${total},${totalKPIOffline},${totalKPIOnline},${totalKPIBot}\n`;
    }

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = generateCsvFileName(`export_sale_by_store_data_`);
    a.click();
    URL.revokeObjectURL(url);
}

function formatNumber(value, code) {
    let val = Number(value || 0);
    return getCurrencyPreAndSuffix(code, val);
}

const Container = styled.div`
    .item {
        white-space: normal;
        word-break: break-word;
    }

    .col-index {
        width: 20px;
        display: inline-block;
    }

    .store-name {
        white-space: break-spaces;
        width: 300px;
    }

    .etsy-sales {
        white-space: break-spaces;
        width: 250px;
    }

    .total-sale,
    .total-cost,
    .total-kpi,
    .total-kpi-offline,
    .total-kpi-online,
    .total-kpi-bot,
    .total-revenue {
        width: 100px;
        display: inline-block;
    }

    .Polaris-DataTable__Cell {
        &.slot {
            padding: 0;
            display: table-cell !important;

            .Polaris-DataTable__Cell {
                background-color: #f9fafb;
                padding-top: 5px;
                padding-bottom: 5px;
            }

            .store-owner {
                display: flex;
                overflow: hidden;
                flex-wrap: wrap;
                white-space: break-spaces;
                padding-left: 16px;
                width: 300px;
            }
        }

        &:has(.owner-store-slot) {
            display: none;
        }
    }
`;

const HeadingWrap = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    margin-left: -1.4rem;

    .Polaris-Heading {
        cursor: pointer;
    }

    .btn-wrap {
        margin-top: 0.2rem;
        margin-right: 0.5rem;
    }
`;
